<script setup lang="ts">
import { computed, reactive, watch } from 'vue';
import {
  APPENDIX_OPTIONS,
  isAppendixSurveyReady,
  normalizeAppendixSurvey,
  resolveIncludedAppendixDocs,
  type BoAppendixDocKey,
  type BoAppendixSurvey,
} from './boAppendixSurvey';

const props = defineProps<{ modelValue: BoAppendixSurvey }>();
const emit = defineEmits<{
  (e: 'update:modelValue', v: BoAppendixSurvey): void;
  (e: 'complete', v: BoAppendixSurvey): void;
}>();

const draft = reactive<BoAppendixSurvey>(normalizeAppendixSurvey(props.modelValue));

watch(() => props.modelValue, (v) => {
  const n = normalizeAppendixSurvey(v);
  draft.selected = [...n.selected];
  draft.completed = n.completed;
}, { deep: true });

function toggle(id: BoAppendixDocKey) {
  const i = draft.selected.indexOf(id);
  if (i >= 0) draft.selected.splice(i, 1);
  else draft.selected.push(id);
  sync();
}
function snapshot(): BoAppendixSurvey {
  return { selected: [...draft.selected], completed: draft.completed };
}
function sync() { emit('update:modelValue', snapshot()); }

const ready = computed(() => isAppendixSurveyReady(draft));
const preview = computed(() => {
  const keys = resolveIncludedAppendixDocs({ ...snapshot(), completed: true });
  return keys.map((k) => APPENDIX_OPTIONS.find((o) => o.id === k)!).filter(Boolean);
});

const groups = [
  { id: 'A' as const, title: 'Appendix A — Self as BO' },
  { id: 'B' as const, title: 'Appendix B — Another person as BO' },
  { id: 'C' as const, title: 'Appendix C — Senior management in place of BO' },
];

function continueToDocs() {
  if (!ready.value) return;
  const next = { ...snapshot(), completed: true };
  emit('update:modelValue', next);
  emit('complete', next);
}
</script>

<template>
  <div class="sv">
    <div class="sv-hd">
      <h2>Beneficial Owner Letters — Appendix A, B &amp; C</h2>
      <p>Select the reply form(s) to prepare. These are separate from s.60C Notices.</p>
    </div>

    <section v-for="g in groups" :key="g.id" class="sv-block">
      <h3>{{ g.title }}</h3>
      <div class="sv-opts">
        <button
          v-for="o in APPENDIX_OPTIONS.filter((x) => x.group === g.id)"
          :key="o.id"
          type="button"
          class="sv-opt"
          :class="{ on: draft.selected.includes(o.id) }"
          @click="toggle(o.id)"
        >
          <span class="sv-check">{{ draft.selected.includes(o.id) ? '✓' : '' }}</span>
          <span class="sv-tx">
            <b>{{ o.label }}</b>
            <small>{{ o.hint }}</small>
          </span>
        </button>
      </div>
    </section>

    <div v-if="preview.length" class="sv-prev">
      <b>{{ preview.length }} form{{ preview.length === 1 ? '' : 's' }}</b>
      <span>{{ preview.map((p) => p.label).join(' · ') }}</span>
    </div>

    <button type="button" class="sv-go" :disabled="!ready" @click="continueToDocs">
      Continue to documents →
    </button>
  </div>
</template>

<style scoped>
.sv {
  width: 794px; max-width: 100%; margin: 0 auto 18px; padding: 18px 16px 20px;
  background: #fff; border: 1px solid #e6ebf3; border-radius: 14px;
  box-shadow: 0 6px 18px rgba(20, 30, 60, .06);
}
.sv-hd h2 { margin: 0 0 4px; font-size: 16px; color: #1f2d4d; }
.sv-hd p { margin: 0 0 14px; font-size: 12.5px; color: #6b778c; }
.sv-block { margin-bottom: 14px; }
.sv-block h3 { margin: 0 0 8px; font-size: 12px; font-weight: 800; color: #42506a; letter-spacing: .02em; }
.sv-opts { display: flex; flex-direction: column; gap: 7px; }
.sv-opt {
  display: flex; align-items: flex-start; gap: 10px; text-align: left;
  padding: 10px 12px; border: 1px solid #e0e8f4; border-radius: 10px;
  background: #f7f9fc; cursor: pointer; transition: .15s;
}
.sv-opt:hover { background: #eef3fb; }
.sv-opt.on { background: #eaf1ff; border-color: #b9d0ff; }
.sv-check {
  flex: none; width: 22px; height: 22px; border-radius: 6px; margin-top: 1px;
  background: #d5dde9; color: #fff; font-weight: 800; font-size: 13px;
  display: inline-flex; align-items: center; justify-content: center;
}
.sv-opt.on .sv-check { background: #1b64da; }
.sv-tx { display: flex; flex-direction: column; gap: 2px; min-width: 0; }
.sv-tx b { font-size: 12.5px; color: #1f2d4d; }
.sv-tx small { font-size: 11px; color: #7a869c; line-height: 1.35; }
.sv-prev {
  display: flex; flex-direction: column; gap: 2px; margin: 12px 0 14px; padding: 10px 12px;
  background: #f4f7fc; border-radius: 10px; border: 1px solid #e0e8f4;
}
.sv-prev b { font-size: 12px; color: #1f2d4d; }
.sv-prev span { font-size: 11px; color: #6b778c; }
.sv-go {
  display: block; width: 100%; border: none; border-radius: 10px; padding: 12px 16px;
  background: #1b64da; color: #fff; font-weight: 800; font-size: 13px; cursor: pointer;
}
.sv-go:disabled { background: #c9d3e4; cursor: not-allowed; }
</style>
